/**
 * Which language the verify flow opens in.
 *
 * Two sources, and the order between them is the point:
 *
 * 1. The applicant's own choice, made with the EN/ไทย toggle on this tab.
 * 2. The language the link was minted with (`language` on the collection-flow
 *    snapshot), i.e. what the integrator knows about their customer.
 *
 * The applicant wins. A Thai bank minting links in Thai must not override an
 * expatriate who switched to English — and a refresh mid-flow must not flip
 * the screen back. The server language is applied only when nobody chose.
 *
 * `sessionStorage`, not `localStorage`, for the same reason as `deviceChoice`:
 * verification devices are shared, and one applicant's choice must not carry
 * into the next one's flow. Every access is wrapped because Safari private mode
 * and hardened profiles throw on storage access; a storage failure degrades to
 * "no remembered choice", never to a thrown render.
 */

export const SUPPORTED_LANGUAGES = ['en', 'th'] as const;
export type FlowLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const KEY = 'trustsy.language';

function isSupported(value: unknown): value is FlowLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** The language the applicant chose on this tab, if any. */
export function chosenLanguage(): FlowLanguage | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isSupported(value) ? value : null;
  } catch {
    return null;
  }
}

/** Remember the applicant's choice for the rest of this tab's sitting. */
export function rememberLanguage(language: string): void {
  if (!isSupported(language)) return;
  try {
    window.sessionStorage.setItem(KEY, language);
  } catch {
    // Not remembered; the choice still applies until the next reload.
  }
}

/**
 * The language to switch to for a snapshot's `language`, or `null` to leave
 * the current one alone (the applicant already chose, or the link carries
 * none, or it carries one this build does not ship).
 */
export function serverLanguageToApply(serverLanguage: unknown): FlowLanguage | null {
  if (chosenLanguage() !== null) return null;
  return isSupported(serverLanguage) ? serverLanguage : null;
}
